import type {IndexCopilotSpace} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useNavigate} from '@github-ui/react-router'
import {ActionList, ActionMenu, Button, Spinner, Stack, Tooltip} from '@primer/react'
import {useState, useRef, useEffect} from 'react'
import {Icon} from '@github-ui/pacer/Icon'

import {useFetchCopilotSpaces, useCustomCopilotsEnabled} from '../hooks'
import {useRecentCopilotSpaces} from '../hooks/use-recent-copilot-spaces'

import {useSpaceSelection} from '../hooks/use-space-selection'
import {COPILOT_SPACES_PATH} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'

import {ToggleButton} from '@github-ui/pacer/ToggleButton'
import {CreateSpaceDialog} from './CreateSpaceDialog'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {SpaceIcon} from '@primer/octicons-react'
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import styles from './SpacesSelect.module.css'

export function SpacesSelect({openCreateSpaceDialog}: {openCreateSpaceDialog?: (show: boolean) => void}) {
  const manager = useChatManager()
  const {usePlanningIntent} = useChatState()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const {handleSpaceSelect: setPendingSpace, clearSpaceSelection, activeCopilotSpaceId, readOnly} = useSpaceSelection()
  const loadingStatusRef = useRef<HTMLDivElement>(null)
  const firstActionItemRef = useRef<HTMLLIElement>(null)

  const [showCreateSpaceDialog, setShowCreateSpaceDialog] = useState(false)

  const handleMenuOpen = (value: boolean) => {
    if (copilotFeatureFlags.spacesAsAttachmentsEnabled && value) {
      sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_COPILOT_SPACES', mode: 'immersive'})
    }
    setOpen(value)
  }

  const closeCreateSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_CREATE_DIALOG_CLOSE', mode: 'immersive'})
    setShowCreateSpaceDialog(false)
  }

  const handleCreateSpace = () => {
    setShowCreateSpaceDialog(true)
    if (openCreateSpaceDialog) {
      openCreateSpaceDialog(true)
    }
    sendEvent('dotcom_chat.activate', {
      target: 'SPACES_INPUT_CREATE_SPACE',
      mode: 'immersive',
      fromAttachmentMenu: copilotFeatureFlags.spacesAsAttachmentsEnabled,
    })
  }

  const handleViewAllSpaces = () => {
    navigate(COPILOT_SPACES_PATH)
    sendEvent('dotcom_chat.activate', {
      target: 'SPACES_INPUT_VIEW_ALL_SPACES',
      mode: 'immersive',
    })
  }

  const handleSpaceSelect = (space: IndexCopilotSpace) => {
    const isCurrentlySelected =
      activeCopilotSpaceId && space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner
    if (isCurrentlySelected) {
      clearSpaceSelection()
    } else {
      if (openCreateSpaceDialog) {
        manager.addReference({...space, type: 'copilot-space'}, 'spaces')
      }
      setPendingSpace({id: space.id, owner: space.owner})
      sendEvent('dotcom_chat.activate', {
        target: 'SPACES_INPUT_SELECT_SPACE',
        space: `${space.owner}/${space.id}`,
        fromAttachmentMenu: copilotFeatureFlags.spacesAsAttachmentsEnabled,
      })
    }
    setOpen(false)
  }

  const {data: sortedSpaces = [], isLoading: isLoadingRecentSpaces} = useRecentCopilotSpaces()
  const {data: allSpaces = [], isLoading: isLoadingAllSpaces} = useFetchCopilotSpaces(true)

  const copilotSpacesEnabled = useCustomCopilotsEnabled()

  const isLoading = isLoadingRecentSpaces || isLoadingAllSpaces

  // Focus the loading status when it becomes visible, and focus first action item when loading completes
  useEffect(() => {
    if (isLoading && open && loadingStatusRef.current) {
      loadingStatusRef.current.focus()
    } else if (!isLoading && open && firstActionItemRef.current) {
      // Small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        firstActionItemRef.current?.focus()
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [isLoading, open])

  // If readOnly (if a thread exists) hide the menu. We can't yet assign
  // a space to an existing thread.
  if (!copilotSpacesEnabled || readOnly) {
    return
  }

  // Spaces is not compatible with planning mode, so disable if planning mode is toggled on
  if (copilotFeatureFlags.planningAgent && usePlanningIntent) {
    return (
      <Tooltip text="Spaces are not compatible with Planning mode.">
        <Button leadingVisual={SpaceIcon} inactive aria-disabled className={styles.disabledSpacesButton}>
          Space
        </Button>
      </Tooltip>
    )
  }

  // Find the selected space in allSpaces
  const selectedSpaceData = activeCopilotSpaceId
    ? allSpaces.find(space => space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner)
    : undefined

  // Use the space's name and blue color if found, otherwise use default
  const displayName = selectedSpaceData ? selectedSpaceData.name : 'Space'

  const menuWidth = '260px'
  return (
    <>
      <ActionMenu open={open} onOpenChange={handleMenuOpen}>
        <ActionMenu.Anchor>
          {copilotFeatureFlags.spacesAsAttachmentsEnabled ? (
            <ActionList.Item aria-label="Select a copilot space for this conversation">
              <ActionList.LeadingVisual>
                <SpaceIcon />
              </ActionList.LeadingVisual>
              Spaces&hellip;
            </ActionList.Item>
          ) : (
            <ToggleButton
              icon={<Icon icon="spaces" />}
              isSelected={!!selectedSpaceData}
              onClick={() => {}} // ActionMenu will handle toggling
              hasMenu
              aria-label="Select a copilot space for this conversation"
              isMenuOpen={open}
              selectedItemLabel={displayName}
            >
              {displayName}
            </ToggleButton>
          )}
        </ActionMenu.Anchor>
        <ActionMenu.Overlay style={sortedSpaces.length > 0 ? {width: menuWidth} : {}}>
          {isLoading ? (
            <Stack direction="horizontal" align="center" justify="center" padding="normal" style={{width: menuWidth}}>
              <div ref={loadingStatusRef} role="status" className="sr-only" tabIndex={-1}>
                Loading spaces
              </div>
              <Spinner />
            </Stack>
          ) : (
            <ActionList selectionVariant="single" role="menu" aria-label="Select a space">
              {sortedSpaces.length > 0 ? (
                <>
                  <ActionList.Group>
                    <ActionList.GroupHeading aria-label="Recent spaces">Recent</ActionList.GroupHeading>
                    {sortedSpaces.map((space, index) => {
                      const isSelected =
                        !!activeCopilotSpaceId &&
                        space.id === activeCopilotSpaceId.id &&
                        space.owner === activeCopilotSpaceId.owner
                      return (
                        <ActionList.Item
                          ref={index === 0 ? firstActionItemRef : undefined}
                          role="menuitemradio"
                          key={`${space.owner}, ${space.id}`}
                          onSelect={() => handleSpaceSelect(space)}
                          selected={isSelected}
                          aria-checked={isSelected}
                        >
                          <span>{space.name}</span>
                        </ActionList.Item>
                      )
                    })}
                  </ActionList.Group>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={handleViewAllSpaces}>View all spaces</ActionList.Item>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={handleCreateSpace}>Create a space</ActionList.Item>
                </>
              ) : (
                <ActionList.Item ref={firstActionItemRef} onSelect={handleCreateSpace}>
                  Create your first space
                </ActionList.Item>
              )}
            </ActionList>
          )}
        </ActionMenu.Overlay>
      </ActionMenu>
      {showCreateSpaceDialog ? <CreateSpaceDialog closeDialog={closeCreateSpaceDialog} /> : null}
    </>
  )
}

try{ SpacesSelect.displayName ||= 'SpacesSelect' } catch {}